import React from 'react';
import PropTypes from 'prop-types';
import {
  LuCheck,
  LuCircleAlert,
  LuCircleCheck,
  LuClipboardList,
  LuLoaderCircle,
  LuPackageOpen,
  LuPlus,
  LuSave,
  LuTrash2,
  LuX,
  LuChevronLeft,
  LuChevronRight,
} from 'react-icons/lu';
import Badge from '../../../../../components/ui/Badge';
import { INPUT_CLASS } from '../../../../../components/form/FormControls';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { StepCircle } from '../../../../OperationManagement/visitModal/VisitStepper';
import { Meter } from '../../../../OperationManagement/visitModal/VisitUi';
import { EVAL_FIELDS, EXTRA_BUTTON, FOOTER, FORM, FORM_FIELDS, missingChipRects, stepRect, todoRowRect } from '../stageLayout';
import CalendarScene from './CalendarScene';

const abs = (rect) => ({ left: rect.x, top: rect.y, width: rect.w, height: rect.h });
const STEPS = ['informations', 'todoList', 'visitForm', 'stock', 'shipments', 'expenses', 'visitEvaluation'];
const TODO_KEYS = ['pre_patientCalled', 'pre_nurseTransferArranged', 'pre_courierArranged', 'pre_hospitalApproval', 'pre_nurseEquipmentReady', 'pre_accommodationPlanned'];
const VISIT_TYPES = ['hastane', 'home', 'telefon', 'unplanning', 'handover'];
export const FORM_MISSING_KEYS = ['visitType', 'sourceDocument', 'doctor', 'hospital', 'city', 'travelType'];
const EVAL_KEYS = ['screenFail', 'dropPatient', 'protocolDeviation', 'processEndDate', 'slaTarget', 'issueOccurred'];

/** What the story has filled in so far → missing fields per step and the overall share. */
export const formProgress = ({ todo = 0, form = {}, answers = {} }) => {
  const formMissing = FORM_MISSING_KEYS.filter((key) => !(key === 'doctor' ? form.doctorDone : form[key]));
  const evalMissing = EVAL_KEYS.filter((key) => !answers[key]);
  const todoMissing = todo > 0 ? 0 : 1;
  // 17 required checks: 1 to-do + 10 form fields (4 prefilled) + 6 evaluation answers
  const missing = todoMissing + formMissing.length + evalMissing.length;
  return { formMissing, evalMissing, todoMissing, missing, percent: Math.round(((17 - missing) / 17) * 100), ready: missing === 0 };
};

const Chip = ({ rect, selected, children }) => (
  <span
    style={abs(rect)}
    className={`tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-border tw-text-[13px] tw-font-semibold tw-transition-colors tw-duration-300 ${
      selected
        ? 'tw-border-teal-500/40 tw-bg-teal-500/15 tw-text-teal-800 dark:tw-text-teal-200'
        : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300'
    }`}
  >
    {selected && <LuCheck className="tw-h-3.5 tw-w-3.5" />}
    <span className="tw-truncate">{children}</span>
  </span>
);

Chip.propTypes = { rect: PropTypes.object.isRequired, selected: PropTypes.bool, children: PropTypes.node };

const Input = ({ rect, value, label, required, missing, focused }) => (
  <>
    <span style={{ left: rect.x + 2, top: rect.y - 19 }} className="tw-absolute tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
      {label}
      {required && <span className="tw-text-rose-500">*</span>}
      {missing && <span className="tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-text-[9px] tw-font-bold tw-uppercase tw-text-amber-800 dark:tw-text-amber-200">!</span>}
    </span>
    {/* The wrapper carries the size: INPUT_CLASS has w-full, which beats an inline width */}
    <span style={abs(rect)} className="tw-absolute">
      <span className={`tw-flex tw-items-center ${INPUT_CLASS} tw-transition-shadow tw-duration-300 ${focused ? 'tw-ring-2 tw-ring-teal-500/60' : ''}`}>
        <span className="tw-truncate tw-text-slate-800 dark:tw-text-slate-100">{value}</span>
        {focused && <span className="tw-ml-px tw-h-4 tw-w-px tw-animate-pulse tw-bg-teal-600" />}
      </span>
    </span>
  </>
);

Input.propTypes = { rect: PropTypes.object.isRequired, value: PropTypes.string, label: PropTypes.string, required: PropTypes.bool, missing: PropTypes.bool, focused: PropTypes.bool };

const Card = ({ rect, title, letter, done, children }) => (
  <div style={abs(rect)} className="tw-absolute tw-rounded-[1.1rem] tw-border tw-border-slate-200/80 tw-bg-white/80 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
    <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-4 tw-pt-2.5">
      {letter && <span className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg tw-bg-teal-500/10 tw-text-xs tw-font-bold tw-text-teal-700 dark:tw-text-teal-300">{letter}</span>}
      <span className="tw-text-[13px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{title}</span>
      {done && <span className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-text-emerald-600 dark:tw-text-emerald-300"><LuCheck className="tw-h-3.5 tw-w-3.5" /></span>}
    </div>
    {children}
  </div>
);

Card.propTypes = { rect: PropTypes.object.isRequired, title: PropTypes.string, letter: PropTypes.string, done: PropTypes.bool, children: PropTypes.node };

/**
 * "Vizit Detayları" as drawn in the video: the step rail, the progress, the
 * page of the current step and the footer, from `state`.
 */
const VisitFormScene = ({ state }) => {
  const { t, locale } = useTranslation();
  const vm = (key, vars) => t(`visitModal.${key}`, vars);
  const stage = (key) => t(`profilePage.tutorial.stage.${key}`);
  const { appear = 1, step = 1, todo = 0, form = {}, answers = {}, chipFocus, fieldFocus, saving, completing, railFocus } = state;
  const progress = formProgress(state);

  const stepState = (key) => {
    if (key === 'informations') return { state: 'info', missing: [] };
    if (key === 'todoList') return progress.todoMissing ? { state: 'missing', missing: [1] } : { state: 'done', missing: [] };
    if (key === 'visitForm') return progress.formMissing.length ? { state: 'missing', missing: progress.formMissing } : { state: 'done', missing: [] };
    if (key === 'visitEvaluation') return progress.evalMissing.length ? { state: 'missing', missing: progress.evalMissing } : { state: 'done', missing: [] };
    return { state: 'optional', missing: [] };
  };
  const statusText = ({ state: s, missing }) => {
    if (s === 'missing') return vm('status.missing', { count: missing.length });
    if (s === 'done') return vm('status.done');
    if (s === 'info') return vm('status.info');
    return vm('status.optional');
  };
  const current = STEPS[step - 1];
  const currentState = stepState(current);
  const missingLabels = current === 'visitForm' ? progress.formMissing.map((key) => vm(`fields.${key}`)) : [];
  const chipRects = missingChipRects(missingLabels);
  const yesNo = (group, value, letterRow) => (
    <>
      <Chip rect={EVAL_FIELDS[group].yes} selected={value === 'yes'}>{vm('common.yes')}</Chip>
      <Chip rect={EVAL_FIELDS[group].no} selected={value === 'no'}>{vm('common.no')}</Chip>
      <span style={{ left: EVAL_FIELDS[group].yes.x + 2, top: letterRow - 18 }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
        {vm(`fields.${group}`)} <span className="tw-text-rose-500">*</span>
      </span>
    </>
  );

  return (
    <>
      <CalendarScene state={state.calendar || {}} />
      <div style={{ opacity: appear }} className="tw-absolute tw-inset-0 tw-bg-slate-950/45 dark:tw-bg-black/60" />
      <div style={{ opacity: appear, transform: `translateY(${(1 - appear) * 24}px)` }} className="tw-absolute tw-inset-0">
        <div style={abs(FORM.dialog)} className="tw-absolute tw-overflow-hidden tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white tw-shadow-2xl dark:tw-border-white/10 dark:tw-bg-[#1f232b]">
          {/* Başlık */}
          <div className="tw-flex tw-h-16 tw-items-center tw-gap-3 tw-border-b tw-border-slate-200/80 tw-px-6 dark:tw-border-white/10">
            <span className="tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-xl tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-white"><LuClipboardList className="tw-h-[18px] tw-w-[18px]" /></span>
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{vm('title')}</p>
              <p className="tw-truncate tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
                ONKO-301 · {vm('subtitle.patient', { id: 'P-1120' })} · {vm('subtitle.visitNo', { n: 4 })}
              </p>
            </div>
            <span className="tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-full tw-text-slate-500"><LuX className="tw-h-[18px] tw-w-[18px]" /></span>
          </div>
          {/* Adım listesi zemini */}
          <div style={{ left: 0, top: 64, width: FORM.rail.w, height: 540 }} className="tw-absolute tw-border-r tw-border-slate-200/80 tw-bg-slate-50/70 dark:tw-border-white/10 dark:tw-bg-white/[0.02]" />
          {/* Alt çubuk zemini */}
          <div style={{ left: 0, top: 604, width: FORM.dialog.w, height: 72 }} className="tw-absolute tw-border-t tw-border-slate-200/80 tw-bg-slate-50/80 dark:tw-border-white/10 dark:tw-bg-white/[0.02]" />
        </div>

        {/* İlerleme */}
        <div style={abs(FORM.progress)} className={`tw-absolute tw-rounded-2xl tw-border tw-bg-white/90 tw-p-3.5 tw-transition-shadow dark:tw-bg-white/[0.04] ${railFocus === 'progress' ? 'tw-border-teal-400 tw-ring-2 tw-ring-teal-400/50' : 'tw-border-slate-200/80 dark:tw-border-white/10'}`}>
          <div className="tw-flex tw-items-baseline tw-justify-between">
            <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{vm('progress.title')}</span>
            <span className={`tw-text-lg tw-font-bold tw-tabular-nums ${progress.ready ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-slate-900 dark:tw-text-white'}`}>{vm('progress.percent', { value: progress.percent })}</span>
          </div>
          <Meter percent={progress.percent} tone={progress.ready ? 'tw-bg-emerald-500' : 'tw-bg-teal-500'} className="tw-mt-2" />
          <p className={`tw-mt-2 tw-text-xs tw-font-medium ${progress.ready ? 'tw-text-emerald-700 dark:tw-text-emerald-300' : 'tw-text-amber-700 dark:tw-text-amber-300'}`}>
            {progress.ready ? vm('progress.ready') : vm('progress.missing', { count: progress.missing })}
          </p>
        </div>

        {/* Adımlar */}
        {STEPS.map((key, index) => {
          const rect = stepRect(index);
          const info = stepState(key);
          const active = index === step - 1;
          return (
            <div key={key} style={abs(rect)} className={`tw-absolute tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-px-2 tw-transition-colors tw-duration-300 ${active ? 'tw-bg-white tw-shadow-sm tw-ring-1 tw-ring-slate-200/80 dark:tw-bg-white/[0.07] dark:tw-ring-white/10' : ''} ${railFocus === 'steps' ? 'tw-ring-2 tw-ring-teal-400/60' : ''}`}>
              <StepCircle state={info.state} number={index + 1} active={active} />
              <span className="tw-min-w-0">
                <span className={`tw-block tw-truncate tw-text-sm ${active ? 'tw-font-semibold tw-text-slate-900 dark:tw-text-white' : 'tw-font-medium tw-text-slate-700 dark:tw-text-slate-200'}`}>{vm(`steps.${key}.label`)}</span>
                <span className={`tw-block tw-truncate tw-text-xs ${info.state === 'missing' ? 'tw-text-amber-700 dark:tw-text-amber-300' : info.state === 'done' ? 'tw-text-emerald-700 dark:tw-text-emerald-300' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>{statusText(info)}</span>
              </span>
            </div>
          );
        })}

        {/* Adım başlığı */}
        <div style={{ left: FORM.content.x, top: FORM.content.y, width: FORM.content.w }} className="tw-absolute">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{vm('progress.stepOf', { current: step, total: 7 })}</span>
            <Badge tone={{ missing: 'warning', done: 'success', info: 'neutral', optional: 'neutral' }[currentState.state]}>{statusText(currentState)}</Badge>
            {['todoList', 'stock', 'shipments', 'expenses'].includes(current) && (
              <Badge tone="accent"><LuCircleCheck className="tw-h-3.5 tw-w-3.5" />{vm('progress.autosave')}</Badge>
            )}
          </div>
          <p className="tw-mt-1 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{vm(`steps.${current}.title`)}</p>
          <p className="tw-mt-1 tw-line-clamp-2 tw-text-[13px] tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{vm(`steps.${current}.help`)}</p>
        </div>

        {/* Eksik alanlar */}
        {missingLabels.length > 0 && (
          <>
            <div style={abs(FORM.missing)} className="tw-absolute tw-rounded-2xl tw-border tw-border-amber-500/25 tw-bg-amber-400/[0.08]" />
            <span style={{ left: 334, top: 208 }} className="tw-absolute tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">
              <LuCircleAlert className="tw-h-4 tw-w-4" />
              {vm('status.missingFields')}
            </span>
            {missingLabels.map((text, index) => (
              <span
                key={text}
                style={abs(chipRects[index])}
                className={`tw-absolute tw-grid tw-place-items-center tw-truncate tw-rounded-full tw-border tw-px-2 tw-text-xs tw-font-medium tw-transition-colors ${chipFocus === progress.formMissing[index] ? 'tw-border-amber-500 tw-bg-amber-400/30 tw-text-amber-950 dark:tw-text-amber-50' : 'tw-border-amber-500/30 tw-bg-white/80 tw-text-amber-900 dark:tw-bg-white/[0.06] dark:tw-text-amber-100'}`}
              >
                {text}
              </span>
            ))}
          </>
        )}

        {current === 'informations' && (
          <div style={{ left: 320, top: 250, width: 896 }} className="tw-absolute tw-grid tw-grid-cols-3 tw-gap-3">
            {[['project', 'ONKO-301'], ['phase', 'Faz 3'], ['centersType', '—'], ['patientId', 'P-1120'], ['visitNumber', vm('info.visitNumberValue', { n: 4 })]].map(([key, value]) => (
              <div key={key} className="tw-rounded-[1.1rem] tw-border tw-border-slate-200/80 tw-bg-white/80 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{vm(`info.${key}`)}</p>
                <p className="tw-mt-0.5 tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{value}</p>
              </div>
            ))}
          </div>
        )}

        {current === 'todoList' && (
          <>
            <div style={{ left: 320, top: 248, width: 896, height: 40 }} className="tw-absolute tw-flex tw-items-center tw-gap-2.5 tw-rounded-2xl tw-border tw-border-violet-500/20 tw-bg-violet-500/[0.06] tw-px-4 tw-text-[13px] tw-text-violet-950 dark:tw-text-violet-100">
              <LuCircleCheck className="tw-h-4 tw-w-4 tw-text-violet-500" />
              <span className="tw-truncate">{vm('todo.autosaveNote')}</span>
            </div>
            <Card rect={{ x: 320, y: 296, w: 896, h: 214 }} title={vm('todo.groups.pre.title')} />
            {TODO_KEYS.map((key, index) => {
              const checked = index < todo;
              const rect = todoRowRect(index);
              return (
                <div key={key} style={abs(rect)} className={`tw-absolute tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-px-3.5 tw-transition-colors tw-duration-300 ${checked ? 'tw-border-teal-500/30 tw-bg-teal-500/[0.06]' : 'tw-border-slate-200/80 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.02]'}`}>
                  <span className={`tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-md tw-border tw-transition-colors tw-duration-300 ${checked ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white' : 'tw-border-slate-300 tw-bg-white tw-text-transparent dark:tw-border-white/25 dark:tw-bg-white/[0.04]'}`}>
                    <LuCheck className="tw-h-3.5 tw-w-3.5" strokeWidth={3} />
                  </span>
                  <span className="tw-truncate tw-text-[13px] tw-text-slate-700 dark:tw-text-slate-200">{vm(`todo.items.${key}`)}</span>
                </div>
              );
            })}
          </>
        )}

        {current === 'visitForm' && (
          <>
            <Card rect={FORM_FIELDS.card1} title={vm('form.sections.type.title')} done={!progress.formMissing.some((key) => ['visitType', 'sourceDocument', 'travelType'].includes(key))} />
            <span style={{ left: 338, top: 287 }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{vm('form.visitType')} <span className="tw-text-rose-500">*</span></span>
            {VISIT_TYPES.map((type, index) => <Chip key={type} rect={FORM_FIELDS.types[index]} selected={form.visitType === type}>{vm(`form.visitTypes.${type}`)}</Chip>)}
            <span style={{ left: 338, top: 346 }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{vm('form.sourceDocument')} <span className="tw-text-rose-500">*</span></span>
            <Chip rect={FORM_FIELDS.sourceYes} selected={form.sourceDocument === 'yes'}>{vm('common.yes')}</Chip>
            <Chip rect={FORM_FIELDS.sourceNo} selected={form.sourceDocument === 'no'}>{vm('common.no')}</Chip>
            <span style={{ left: 562, top: 346 }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{vm('form.travelType')} <span className="tw-text-rose-500">*</span></span>
            <Chip rect={FORM_FIELDS.travelLocal} selected={form.travelType === 'local'}>{vm('form.travelTypes.local')}</Chip>
            <Chip rect={FORM_FIELDS.travelOutside} selected={form.travelType === 'outside'}>{vm('form.travelTypes.outside')}</Chip>

            <Card rect={FORM_FIELDS.card2} title={vm('form.sections.time.title')} done />
            <Input rect={FORM_FIELDS.startTime} label={vm('form.startTime')} required value="09:00" />
            <Input rect={FORM_FIELDS.endTime} label={vm('form.endTime')} required value="10:30" />

            <Card rect={FORM_FIELDS.card3} title={`${vm('form.sections.patient.title')} · ${vm('form.sections.location.title')}`} done={!progress.formMissing.some((key) => ['doctor', 'hospital', 'city'].includes(key))} />
            <Input rect={FORM_FIELDS.patientId} label={vm('form.patientId')} required value="P-1120" />
            <Input rect={FORM_FIELDS.doctor} label={vm('form.doctor')} required missing={!form.doctorDone} value={form.doctor || ''} focused={fieldFocus === 'doctor'} />
            <Input rect={FORM_FIELDS.hospital} label={vm('form.hospital')} required missing={!form.hospital} value={form.hospital ? stage('hospital') : ''} focused={fieldFocus === 'hospital'} />
            <Input rect={FORM_FIELDS.city} label={vm('form.city')} required missing={!form.city} value={form.city ? stage('city') : ''} focused={fieldFocus === 'city'} />
          </>
        )}

        {['stock', 'shipments', 'expenses'].includes(current) && (
          <>
            <div style={{ left: 320, top: 248, width: 896, height: 40 }} className="tw-absolute tw-flex tw-items-center tw-gap-2.5 tw-rounded-2xl tw-border tw-border-sky-500/20 tw-bg-sky-500/[0.06] tw-px-4 tw-text-[13px] tw-text-sky-950 dark:tw-text-sky-100">
              <span className="tw-truncate">{current === 'stock' ? vm('stock.optionalNote') : vm(`steps.${current}.help`)}</span>
            </div>
            <span style={abs(EXTRA_BUTTON)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
              <LuPlus className="tw-h-4 tw-w-4" />
              {vm({ stock: 'stock.add', shipments: 'shipments.add', expenses: 'expenses.add' }[current])}
            </span>
            <div style={{ left: 320, top: 352, width: 896, height: 200 }} className="tw-absolute tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-[1.1rem] tw-border tw-border-slate-200/80 tw-bg-white/80 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
              <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05]"><LuPackageOpen className="tw-h-7 tw-w-7" /></span>
              <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{vm({ stock: 'stock.emptyTitle', shipments: 'shipments.emptyTitle', expenses: 'expenses.emptyTitle' }[current])}</p>
            </div>
          </>
        )}

        {current === 'visitEvaluation' && (
          <>
            <div style={{ left: 320, top: 248 }} className="tw-absolute tw-flex tw-gap-2">
              <span className="tw-rounded-full tw-bg-slate-500/[0.08] tw-px-3.5 tw-py-1.5 tw-text-[13px] tw-text-slate-600 dark:tw-text-slate-300">{vm('evaluation.summary.visitType')}: <strong className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{vm('form.visitTypes.hastane')}</strong></span>
              <span className="tw-rounded-full tw-bg-slate-500/[0.08] tw-px-3.5 tw-py-1.5 tw-text-[13px] tw-text-slate-600 dark:tw-text-slate-300">{vm('evaluation.summary.duration')}: <strong className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{vm('evaluation.summary.durationValue', { value: '1.50' })}</strong></span>
            </div>
            <Card rect={EVAL_FIELDS.cards[0]} letter="B" title={vm('evaluation.site.B.title')} done={Boolean(answers.screenFail && answers.dropPatient)} />
            {yesNo('screenFail', answers.screenFail, 322)}
            {yesNo('dropPatient', answers.dropPatient, 322)}
            <Card rect={EVAL_FIELDS.cards[1]} letter="C" title={vm('evaluation.site.C.title')} done={Boolean(answers.protocolDeviation)} />
            {yesNo('protocolDeviation', answers.protocolDeviation, 404)}
            <Card rect={EVAL_FIELDS.cards[2]} letter="E" title={vm('evaluation.site.E.title')} done={Boolean(answers.processEndDate && answers.slaTarget)} />
            <Input rect={EVAL_FIELDS.processEndDate} label={vm('fields.processEndDate')} required value={answers.processEndDate ? new Date().toLocaleDateString(locale) : ''} focused={fieldFocus === 'processEndDate'} />
            <Input rect={EVAL_FIELDS.slaTarget} label={vm('fields.slaTarget')} required value={answers.slaTarget ? '5' : ''} focused={fieldFocus === 'slaTarget'} />
            <Card rect={EVAL_FIELDS.cards[3]} letter="F" title={vm('evaluation.site.F.title')} done={Boolean(answers.issueOccurred)} />
            {yesNo('issueOccurred', answers.issueOccurred, 568)}
          </>
        )}

        {/* Alt çubuk */}
        <span style={abs(FOOTER.close)} className="tw-absolute tw-grid tw-place-items-center tw-text-sm tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{vm('actions.close')}</span>
        <span style={abs(FOOTER.remove)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-border tw-border-rose-500/25 tw-bg-rose-500/10 tw-text-xs tw-font-semibold tw-text-rose-700 dark:tw-text-rose-300"><LuTrash2 className="tw-h-3.5 tw-w-3.5" />{vm('actions.delete')}</span>
        <span style={abs(FOOTER.back)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-rounded-full tw-border tw-border-white tw-bg-white/80 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-200"><LuChevronLeft className="tw-h-4 tw-w-4" />{vm('actions.back')}</span>
        <span style={abs(FORM.next)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-rounded-full tw-border tw-border-white tw-bg-white/80 tw-text-sm tw-font-semibold tw-text-slate-700 tw-shadow-sm dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-text-slate-200">{vm('actions.next')}<LuChevronRight className="tw-h-4 tw-w-4" /></span>
        <span style={abs(FORM.save)} className="tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-b tw-from-teal-400 tw-to-cyan-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
          {saving ? <LuLoaderCircle className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave className="tw-h-4 tw-w-4" />}
          {vm('actions.save')}
        </span>
        <span style={abs(FORM.complete)} className={`tw-absolute tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-b tw-from-emerald-400 tw-to-emerald-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-emerald-500/30 ${progress.ready ? '' : 'tw-opacity-90'}`}>
          {completing ? <LuLoaderCircle className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuCircleCheck className="tw-h-4 tw-w-4" />}
          {completing ? vm('actions.completing') : vm('actions.complete')}
        </span>
      </div>
    </>
  );
};

VisitFormScene.propTypes = { state: PropTypes.object.isRequired };

export default VisitFormScene;
